import { create } from "zustand";

export const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/jpg",
];

interface PromptInputActions {
  setPrompt: (prompt: string | null) => void;
  setAttachedImage: (image: File | null) => void;
  setIsSubmitting: (isSubmitting: boolean) => void;
  isLengthExceeded: () => boolean;
  saveInLocalStorage: () => void;
  loadFromLocalStorage: () => void;
  clear: () => void;
}

interface PromptInputState {
  maxPromptLength: number;
  prompt: string | null;
  attachedImage: File | null;
  isSubmitting: boolean;

  actions: PromptInputActions;
}

const usePromptInputStore = create<PromptInputState>((set, get) => ({
  maxPromptLength: 10000,
  prompt: null,
  attachedImage: null,
  isSubmitting: false,

  actions: {
    setPrompt: (prompt) => set({ prompt }),

    setAttachedImage: (attachedImage) => set({ attachedImage }),

    setIsSubmitting: (isSubmitting) => set({ isSubmitting }),

    isLengthExceeded: () =>
      get().prompt !== null && get().prompt!.length > get().maxPromptLength,

    saveInLocalStorage: () => {
      if (get().prompt !== null && get().prompt !== "") {
        localStorage.setItem("prompt", get().prompt!);
      }

      const attachedImage = get().attachedImage;
      if (attachedImage) {
        const reader = new FileReader();
        reader.onload = () => {
          const base64String = reader.result as string;
          localStorage.setItem("attachedImage", base64String);
          localStorage.setItem("attachedImageName", attachedImage.name);
          localStorage.setItem("attachedImageType", attachedImage.type);
        };
        reader.readAsDataURL(attachedImage);
      }
    },

    loadFromLocalStorage: async () => {
      const savedPrompt = localStorage.getItem("prompt");
      let attachedImage: File | null = null;

      const savedImageBase64 = localStorage.getItem("attachedImage");
      const savedImageName = localStorage.getItem("attachedImageName");
      const savedImageType = localStorage.getItem("attachedImageType");
      if (savedImageBase64 && savedImageName && savedImageType) {
        const res = await fetch(savedImageBase64);
        const blob = await res.blob();
        attachedImage = new File([blob], savedImageName, {
          type: savedImageType,
        });
      }

      set({
        prompt: savedPrompt ?? get().prompt,
        attachedImage: attachedImage ?? get().attachedImage,
      });

      localStorage.removeItem("prompt");
      localStorage.removeItem("attachedImage");
      localStorage.removeItem("attachedImageName");
      localStorage.removeItem("attachedImageType");
    },

    clear: () => set({ prompt: null, attachedImage: null }),
  },
}));

export const useMaxPromptLength = () =>
  usePromptInputStore((state) => state.maxPromptLength);
export const usePrompt = () => usePromptInputStore((state) => state.prompt);
export const useAttachedImage = () =>
  usePromptInputStore((state) => state.attachedImage);
export const useIsPromptSubmitting = () =>
  usePromptInputStore((state) => state.isSubmitting);
export const usePromptInputActions = () =>
  usePromptInputStore((state) => state.actions);
